import { useMutation, useQueryClient } from '@tanstack/react-query';
import useAuth from '../utils/useAuth';
import { axiosPostV3 } from '../../api/axios-handler';
import { userQueryKeys, User } from './useUser';
import { TopicPreference } from './useTopicPreferences';

interface TopicPreferenceInput {
  tag_group_id: number;
  sort_order?: number | null;
}

interface TopicPreferencesPayload {
  preferences: TopicPreferenceInput[];
}

const updateTopicPreferences = async (
  userId: string,
  preferences: TopicPreferenceInput[],
): Promise<TopicPreference[]> => {
  const payload: TopicPreferencesPayload = {
    preferences,
  };
  const response = await axiosPostV3(`/users/${userId}/topic_preferences`, payload);
  return response.data;
};

export const useUpdateTopicPreferences = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: (preferences: TopicPreferenceInput[]) => {
      if (!user?.id) {
        throw new Error('User ID is required');
      }
      return updateTopicPreferences(user.id.toString(), preferences);
    },
    onMutate: async (preferences: TopicPreferenceInput[]) => {
      await queryClient.cancelQueries(userQueryKeys.me);
      const previousUser = queryClient.getQueryData<User>(userQueryKeys.me);

      if (previousUser) {
        queryClient.setQueryData<User>(userQueryKeys.me, (old) => {
          if (!old) return old;
          // Create optimistic topic preferences from the input
          const optimisticPreferences: TopicPreference[] = preferences.map((pref, index) => ({
            id: Date.now() + index,
            tag_group_id: pref.tag_group_id,
            sort_order: pref.sort_order ?? null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          }));

          return {
            ...old,
            caller_role: {
              ...old.caller_role,
              topic_preferences: optimisticPreferences,
            },
          };
        });
      }

      return { previousUser };
    },
    onError: (err, preferences, context) => {
      if (context?.previousUser) {
        queryClient.setQueryData(userQueryKeys.me, context.previousUser);
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData<User>(userQueryKeys.me, (old) => {
        if (!old) return old;
        return {
          ...old,
          caller_role: {
            ...old.caller_role,
            topic_preferences: data,
          },
        };
      });
    },
  });
};
